import React from 'react';
import { Music } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';

const ROW_HEIGHT_PX = 84;   // thumbnail + padding per row
const VISIBLE_ROWS   = 8;   // show up to 8 before scroll kicks in

function formatDuration(seconds) {
  if (!seconds && seconds !== 0) return '';
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

/**
 * Right-side drawer to pick a song.
 * - Minimal: tap row = select + close
 * - No play/delete/details buttons
 * - ~8 rows visible, scrolls after
 */
export default function SongPickerDrawer({ open, onOpenChange, songs, currentSongId, onSelect }) {
  const handlePick = (song) => {
    onSelect(song);
    // tiny delay so the highlight registers
    setTimeout(() => onOpenChange(false), 120);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-zinc-950 border-l border-white/10 text-white w-full sm:max-w-md flex flex-col"
        data-testid="song-picker-drawer"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="text-white text-xl">Choose a Song</SheetTitle>
          <SheetDescription className="text-zinc-400 text-sm">
            Tap any song to select it.
          </SheetDescription>
        </SheetHeader>

        {songs.length === 0 ? (
          <div className="mt-8 text-center py-10">
            <Music className="h-12 w-12 mx-auto mb-3 text-zinc-700" />
            <p className="text-zinc-400 text-sm">No completed songs yet.</p>
          </div>
        ) : (
          <div
            className="mt-6 overflow-y-auto pr-1 -mr-1 flex flex-col gap-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent"
            style={{ maxHeight: `${ROW_HEIGHT_PX * VISIBLE_ROWS}px` }}
          >
            {songs.map((song) => {
              const selected = currentSongId === song.song_id;
              return (
                <button
                  key={song.song_id}
                  type="button"
                  onClick={() => handlePick(song)}
                  data-testid={`song-picker-row-${song.song_id}`}
                  className={`flex items-center gap-3 p-2 rounded-xl border text-left transition-all ${
                    selected
                      ? 'border-[#915ee9] bg-[#915ee9]/10 shadow-[0_0_0_1px_rgba(145,94,233,0.25)]'
                      : 'border-white/8 bg-white/[0.02] hover:border-[#915ee9]/40 hover:bg-[#915ee9]/[0.04]'
                  }`}
                >
                  {/* Thumbnail */}
                  <div className="w-[60px] h-[60px] rounded-lg overflow-hidden flex-shrink-0 bg-zinc-900">
                    {song.image_url ? (
                      <img
                        src={song.image_url}
                        alt={song.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="h-5 w-5 text-zinc-700" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium truncate ${
                        selected ? 'text-white' : 'text-zinc-100'
                      }`}
                    >
                      {song.title || 'Untitled'}
                    </p>
                    {song.duration ? (
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {formatDuration(song.duration)}
                      </p>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
